/**
 * Outcome module DTOs (§9, §15.2): the fairness bundle (§9.1), public results, live draw events, the
 * participant's own reveal result and the admin draw audit.
 *
 * The fairness bundle is a public artifact. Its keys follow §9.1 verbatim (snake_case) so third parties can
 * match the Bible without a mapping table (D-102). Ids and money are strings; hashes are lower-case hex.
 */
import { z } from 'zod';
import { HexBytes32, HexHash256, IsoDateTime, Ulid } from './common.js';
import { DRAW_ALGORITHM, DrawStatus, LotteryMechanic, PrizeTier, WinSource } from './enums.js';
import { MoneyDto } from './money.js';
export const REFERENCE_VERIFIER_VERSION = 'windrop-verifier/1';
// ---------------------------------------------------------------------------------------------------------
// Canonical snapshot (§9.1 step 2–3) — what gets JCS-serialized and hashed
// ---------------------------------------------------------------------------------------------------------
/** One eligible entry in the frozen snapshot: a public entry reference and its allocation weight; no contact details. */
export const SnapshotEntry = z.object({
    entry_id: Ulid,
    /** Weight ≥ 1: an entry with weight w occupies w consecutive tickets in the shuffle (bonus entries weigh 1 each). */
    weight: z.number().int().min(1),
    /** Number pick only: the immutable selections stored on the receipt (§9.3), one per purchased pick. */
    selections: z.array(z.array(z.number().int())).optional(),
});
export const SnapshotTier = z.object({
    tier: PrizeTier,
    prize_id: Ulid,
    winners: z.number().int().min(1),
});
export const SnapshotMatchTier = z.object({
    matches: z.number().int().min(1),
    prize_id: Ulid,
    prize_units: z.number().int().min(1),
});
/** Excluded entries are listed with reason codes (§9.1 step 2) but carry no weight. */
export const SnapshotExclusion = z.object({
    entry_id: Ulid,
    reason: z.enum(['PENDING', 'REFUNDED', 'VOID', 'DISQUALIFIED', 'LATE_PAYMENT', 'INELIGIBLE']),
});
export const CanonicalSnapshot = z.object({
    algorithm: z.literal(DRAW_ALGORITHM),
    lottery_id: Ulid,
    config_version: z.number().int().min(1),
    draw_id: Ulid,
    closed_at: IsoDateTime,
    /** Ascending `entry_id` order (§9.1 step 2). */
    entries: z.array(SnapshotEntry),
    excluded: z.array(SnapshotExclusion).default([]),
    /** Scheduled raffle: published tiers in fixed order. */
    tiers: z.array(SnapshotTier).optional(),
    /** Whether an entry may win more than one tier. */
    allow_multiple_tier_wins: z.boolean().optional(),
    /** Number pick: published range, pick count and match tiers. */
    number_pick: z
        .object({
        range_min: z.number().int(),
        range_max: z.number().int(),
        pick_count: z.number().int().min(1),
        match_tiers: z.array(SnapshotMatchTier),
        /** §9.3 treatment of multiple winning entries in one tier. */
        multiple_winners_policy: z.enum(['EACH_FIXED_UNIT', 'SHARE_TIER_UNITS']).default('EACH_FIXED_UNIT'),
    })
        .optional(),
});
// ---------------------------------------------------------------------------------------------------------
// Winner allocations and bundle
// ---------------------------------------------------------------------------------------------------------
export const WinnerAllocation = z.object({
    tier: PrizeTier,
    prize_id: Ulid,
    entry_id: Ulid,
    /** Index into the shuffled ticket order that produced this winner. */
    ticket_index: z.number().int().min(0),
    /** 1-based rank within the tier. */
    rank: z.number().int().min(1),
});
export const NumberPickAllocation = z.object({
    tier_matches: z.number().int().min(1),
    prize_id: Ulid,
    entry_id: Ulid,
    /** Index of the selection within the entry when an entry holds several selections. */
    selection_index: z.number().int().min(0),
    matched: z.array(z.number().int()),
    /** 1-based rank within the tier (ascending entry_id, then selection index). */
    rank: z.number().int().min(1),
    /** False when the tier's fixed units ran out before this rank (EACH_FIXED_UNIT policy). */
    prize_unit_awarded: z.boolean(),
});
export const BundleTimestamps = z.object({
    committed_at: IsoDateTime,
    closed_at: IsoDateTime.optional(),
    drawn_at: IsoDateTime,
    published_at: IsoDateTime.optional(),
    revealed_at: IsoDateTime.optional(),
});
const bundleBase = {
    algorithm: z.literal(DRAW_ALGORITHM),
    lottery_id: Ulid,
    config_version: z.number().int().min(1),
    draw_id: Ulid,
    seed_commit: HexHash256,
    snapshot_hash: HexHash256,
    result_hash: HexHash256,
    revealed_seed: HexBytes32,
    /** The exact JCS string that was hashed — verifiers re-hash this, never a re-serialization. */
    canonical_snapshot: z.string().min(2),
    published_terms_hash: HexHash256,
    timestamps: BundleTimestamps,
    reference_verifier_version: z.string().min(1),
};
/** §9.1 scheduled-draw bundle. */
export const ScheduledFairnessBundle = z.object({
    ...bundleBase,
    kind: z.literal('SCHEDULED'),
    winner_allocations: z.array(WinnerAllocation),
});
/** §9.2 fixed-pool bundle: the committed pool configuration, pool hash and the revealed shuffled vector. */
export const FixedPoolFairnessBundle = z.object({
    ...bundleBase,
    kind: z.literal('FIXED_POOL'),
    pool_id: Ulid,
    pool_size: z.number().int().min(1),
    /** Exact outcome vector spec committed at publication: prize id (or null for a loss) and count. */
    outcome_spec: z.array(z.object({ prize_id: Ulid.nullable(), count: z.number().int().min(1) })),
    /** SHA-256(JCS(shuffled vector)) committed before entries open. */
    pool_hash: HexHash256,
    /** The revealed shuffled vector: prize id per ticket position, null for a loss. */
    shuffled_vector: z.array(Ulid.nullable()),
    /** Tickets consumed (position → entry id), in assignment order. */
    assignments: z.array(z.object({ position: z.number().int().min(0), entry_id: Ulid })),
    winner_allocations: z.array(z.object({
        prize_id: Ulid,
        entry_id: Ulid,
        ticket_index: z.number().int().min(0),
        rank: z.number().int().min(1),
    })),
});
/** §9.3 number-pick bundle. */
export const NumberPickFairnessBundle = z.object({
    ...bundleBase,
    kind: z.literal('NUMBER_PICK'),
    winning_numbers: z.array(z.number().int()).min(1),
    winner_allocations: z.array(NumberPickAllocation),
});
export const FairnessBundleSchema = z.discriminatedUnion('kind', [
    ScheduledFairnessBundle,
    FixedPoolFairnessBundle,
    NumberPickFairnessBundle,
]);
/** Public `GET /v1/draws/:id/fairness` before reveal: commitment only (no seed, no vector). */
export const FairnessCommitmentDto = z.object({
    draw_id: Ulid,
    lottery_id: Ulid,
    config_version: z.number().int(),
    algorithm: z.literal(DRAW_ALGORITHM),
    seed_commit: HexHash256,
    committed_at: IsoDateTime,
    snapshot_hash: HexHash256.nullable(),
    result_hash: HexHash256.nullable(),
    published_terms_hash: HexHash256,
    status: DrawStatus,
    reference_verifier_version: z.string(),
    /** Present once the seed is revealed. */
    bundle: FairnessBundleSchema.optional(),
    /** Download URL of the bundle JSON once revealed. */
    bundle_url: z.string().optional(),
    kind: z.enum(['SCHEDULED', 'FIXED_POOL', 'NUMBER_PICK']).optional(),
    lottery: z.object({ slug: z.string(), title: z.string(), mechanic: LotteryMechanic }).optional(),
    pool: z
        .object({
        pool_id: Ulid,
        pool_size: z.number().int(),
        pool_hash: HexHash256,
        tickets_assigned: z.number().int(),
    })
        .optional(),
    timestamps: BundleTimestamps.partial().optional(),
    redraw_of_draw_id: Ulid.nullable().optional(),
    /** Server-side verifier result once the draw reached VERIFIED. */
    verification: z
        .object({ ok: z.boolean(), checked_at: IsoDateTime, verifier_version: z.string() })
        .nullable()
        .optional(),
    /** How to reproduce the draw independently (§9.4 reference verifier). */
    how_to_verify: z
        .object({
        command: z.string(),
        verifier_url: z.string().optional(),
        browser_verifier_url: z.string().optional(),
    })
        .optional(),
});
// ---------------------------------------------------------------------------------------------------------
// Results and reveal
// ---------------------------------------------------------------------------------------------------------
/** Public winner display honours publicity consent: anonymous or initials-only by default (§2.4). */
export const PublicWinnerDto = z.object({
    tier: PrizeTier,
    prizeId: Ulid,
    prizeName: z.string(),
    /** `entry_id` is public in the snapshot already; display is anonymized unless consented. */
    entryId: Ulid,
    display: z.string(),
    sponsorName: z.string().optional(),
    source: WinSource,
    /** Public win id (what winners send on social, §12.2) — never a contact detail. */
    winId: z.string().optional(),
    /** 1-based rank inside the tier (fairness bundle `rank`). */
    rank: z.number().int().optional(),
    /** Ticket index in the shuffled order / pool position (fairness bundle `ticket_index`). */
    ticketIndex: z.number().int().optional(),
});
/** One published tier of a results page: committed total, winners revealed so far (operator-paced, §13.2). */
export const PublicTierDto = z.object({
    id: z.string(),
    tier: PrizeTier,
    prizeId: Ulid,
    prizeName: z.string(),
    sponsorName: z.string().optional(),
    /** Committed number of winners in the tier. */
    total: z.number().int(),
    /** Winners revealed so far (≤ total). */
    winners: z.array(PublicWinnerDto),
    revealed: z.boolean(),
});
/** Participant lookup on the results page (`?entryId=`): the outcome of one entry, no contact details. */
export const ResultsLookupDto = z.object({
    entryId: Ulid,
    included: z.boolean(),
    outcome: z.enum(['WIN', 'LOSS', 'SPONSOR_SAMPLE', 'PENDING', 'EXCLUDED']),
    winId: z.string().optional(),
    prizeName: z.string().optional(),
    revealUrl: z.string(),
});
/** `GET /v1/l/:slug/results` — static result with polling fallback (§4.4). */
export const ResultsDto = z.object({
    lotteryId: Ulid,
    slug: z.string(),
    mechanic: LotteryMechanic,
    drawId: Ulid.nullable(),
    drawStatus: DrawStatus.nullable(),
    publishedAt: IsoDateTime.nullable(),
    revealedAt: IsoDateTime.nullable(),
    winners: z.array(PublicWinnerDto),
    winningNumbers: z.array(z.number().int()).optional(),
    totals: z.object({
        eligibleEntries: z.number().int(),
        winners: z.number().int(),
        sponsorSamples: z.number().int(),
    }),
    /** Lottery title for the page heading. */
    title: z.string().optional(),
    timezone: z.string().optional(),
    drawAt: IsoDateTime.optional(),
    closesAt: IsoDateTime.optional(),
    lotteryStatus: z.string().optional(),
    /** Tiers in published order with the winners revealed so far (§4.2 D suspense pacing). */
    tiers: z.array(PublicTierDto).optional(),
    revealedTiers: z.number().int().optional(),
    totalTiers: z.number().int().optional(),
    /** Fixed pools: tickets consumed of the committed pool size (never per-entry odds, §9.2). */
    pool: z.object({ poolSize: z.number().int(), ticketsAssigned: z.number().int() }).optional(),
    resultsUrl: z.string().optional(),
    fairnessUrl: z.string().optional(),
    /** Present when the request carried `?entryId=`: that entry's outcome. */
    mine: ResultsLookupDto.optional(),
    fairness: FairnessCommitmentDto.pick({
        draw_id: true,
        seed_commit: true,
        snapshot_hash: true,
        result_hash: true,
        algorithm: true,
    }).nullable(),
    /** Detached signature over the JCS of this payload minus `signature` (base64url); key id in `signatureKeyId`. */
    signature: z.string().optional(),
    signatureKeyId: z.string().optional(),
    serverNow: IsoDateTime,
});
/** Participant's own outcome for an entry (fetched before any reveal animation starts — §4.2 D). */
export const RevealOutcome = z.enum(['WIN', 'LOSS', 'SPONSOR_SAMPLE', 'PENDING']);
export const RevealResultDto = z.object({
    entryId: Ulid,
    outcome: RevealOutcome,
    mechanic: LotteryMechanic,
    /** Pool ticket position / milestone position / draw rank when applicable. */
    position: z.number().int().optional(),
    winId: z.string().optional(),
    prize: z
        .object({
        prizeId: Ulid,
        name: z.string(),
        tier: PrizeTier,
        value: MoneyDto,
        sponsorName: z.string().optional(),
        accentColor: z.string().optional(),
        heroImageUrl: z.string().optional(),
    })
        .optional(),
    claimDeadline: IsoDateTime.optional(),
    claimUrl: z.string().optional(),
    /** Rendered participant copy: win line, loss line or the sponsor-sample line (§4.2 E/F). */
    headline: z.string(),
    body: z.string().optional(),
    /** Loss: one quiet re-entry action with its cost and closing time, when lawful. */
    reentry: z.object({ cost: MoneyDto, closesAt: IsoDateTime, url: z.string() }).optional(),
    /** Number pick: the entry's selections and the matched numbers. */
    numberPick: z
        .object({
        selections: z.array(z.array(z.number().int())),
        winningNumbers: z.array(z.number().int()),
        bestMatch: z.number().int(),
    })
        .optional(),
    wheelSegmentIndex: z.number().int().optional(),
    /** Wheel renderers: the published segment list (labels only, never probabilities, §5.1). */
    wheelSegments: z
        .array(z.object({ id: z.string(), label: z.string(), prizeId: Ulid.nullable() }))
        .optional(),
    energyLevel: z.enum(['FULL', 'CALM']),
    lotteryId: Ulid.optional(),
    slug: z.string().optional(),
    lotteryTitle: z.string().optional(),
    revealStyle: z
        .enum(['DRAW_ROOM', 'WHEEL', 'SCRATCH', 'INSTANT_CARD', 'MILESTONE_COUNTER', 'NUMBER_BOARD'])
        .optional(),
    drawId: Ulid.nullable().optional(),
    drawStatus: DrawStatus.nullable().optional(),
    /** Pool ticket / sequence reference printed with the outcome (§4.3 "persistent textual outcome"). */
    ticketRef: z.string().optional(),
    tierName: z.string().optional(),
    /** Verified entry record shown on the loss screen (§4.2 F). */
    entry: z
        .object({
        confirmedAt: IsoDateTime.nullable(),
        entriesCount: z.number().int(),
        source: z.enum(['FREE', 'PAID', 'BONUS']),
        status: z.string(),
    })
        .optional(),
    /** Sponsor-default consolation detail (§8, §4.2 E). */
    sponsorSample: z
        .object({
        ruleId: Ulid,
        ruleVersion: z.number().int(),
        sponsorId: Ulid,
        sponsorName: z.string(),
        sponsorLogoUrl: z.string().optional(),
        prizeId: Ulid,
        prizeName: z.string(),
        prizeImageUrl: z.string().optional(),
        value: MoneyDto,
        position: z.number().int(),
        rangeStart: z.number().int(),
        rangeEnd: z.number().int(),
        mainPrizeName: z.string(),
    })
        .optional(),
    /** Sponsor credit for sponsored prizes / reveal skin (§7.1). */
    sponsor: z.object({ sponsorId: Ulid, name: z.string(), logoUrl: z.string().optional() }).optional(),
    /** First-N: milestone map and the real qualified count. */
    milestone: z
        .object({
        rangeEnd: z.number().int(),
        qualifiedCount: z.number().int(),
        milestones: z.array(z.object({ at: z.number().int(), label: z.string() })),
        rule: z.string(),
    })
        .optional(),
    resultsUrl: z.string().optional(),
    fairnessUrl: z.string().optional(),
    /** Scheduled mechanics before the draw: when results are expected. */
    pendingUntil: IsoDateTime.optional(),
    serverNow: IsoDateTime.optional(),
});
/** WebSocket `/draws/:id/live` frames; every frame is reproducible from the static results. */
export const LiveDrawEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal('status'), drawId: Ulid, status: DrawStatus, serverNow: IsoDateTime }),
    z.object({ type: z.literal('countdown'), drawId: Ulid, drawAt: IsoDateTime, serverNow: IsoDateTime }),
    z.object({
        type: z.literal('tier_reveal'),
        drawId: Ulid,
        tier: PrizeTier,
        prizeId: Ulid,
        winners: z.array(PublicWinnerDto),
        index: z.number().int(),
        total: z.number().int(),
    }),
    z.object({ type: z.literal('numbers_reveal'), drawId: Ulid, winningNumbers: z.array(z.number().int()) }),
    z.object({ type: z.literal('complete'), drawId: Ulid, resultsUrl: z.string(), fairnessUrl: z.string() }),
    z.object({
        type: z.literal('entry_count'),
        lotteryId: Ulid,
        confirmedEntries: z.number().int(),
        serverNow: IsoDateTime,
    }),
]);
// ---------------------------------------------------------------------------------------------------------
// Admin draw audit (`GET /v1/admin/draws/:id/audit`)
// ---------------------------------------------------------------------------------------------------------
export const DrawAuditDto = z.object({
    drawId: Ulid,
    lotteryId: Ulid,
    status: DrawStatus,
    configVersion: z.number().int(),
    algorithm: z.literal(DRAW_ALGORITHM),
    seedCommit: HexHash256,
    snapshotHash: HexHash256.nullable(),
    resultHash: HexHash256.nullable(),
    revealedAt: IsoDateTime.nullable(),
    redrawOf: Ulid.nullable(),
    reason: z.string().nullable(),
    operators: z.array(z.object({ staffId: Ulid, role: z.string(), at: IsoDateTime })),
    counts: z.object({
        eligible: z.number().int(),
        excluded: z.number().int(),
        winners: z.number().int(),
        sponsorSamples: z.number().int(),
    }),
    verification: z
        .object({ ok: z.boolean(), checkedAt: IsoDateTime, mismatches: z.array(z.string()) })
        .nullable(),
    timeline: z.array(z.object({ status: DrawStatus, at: IsoDateTime, actorId: z.string(), auditId: Ulid })),
});
export const WinDto = z.object({
    winId: z.string(),
    lotteryId: Ulid,
    entryId: Ulid,
    prizeId: Ulid,
    source: WinSource,
    tier: PrizeTier,
    drawId: Ulid.nullable(),
    samplingRuleId: Ulid.nullable(),
    participantSequence: z.number().int().nullable(),
    ticketPosition: z.number().int().nullable(),
    claimDeadline: IsoDateTime,
    createdAt: IsoDateTime,
});
// ---------------------------------------------------------------------------------------------------------
// Outcome module extensions (outcome agent, D-1401): draw control room, reveal pacing, redraws, verification
// ---------------------------------------------------------------------------------------------------------
export const DrawKind = z.enum(['SCHEDULED', 'FIXED_POOL', 'NUMBER_PICK', 'FIRST_N']);
/** One tier of the control-room reveal ladder (§16.2 "reveal rehearsal", §13.2 operator-controlled). */
export const DrawTierStateDto = z.object({
    id: z.string(),
    tier: PrizeTier,
    prizeId: Ulid,
    prizeName: z.string(),
    total: z.number().int(),
    allocated: z.number().int(),
    revealed: z.boolean(),
});
/** `GET /v1/admin/draws/:id` and rows of `GET /v1/admin/draws` (control room). */
export const DrawAdminDto = z.object({
    drawId: Ulid,
    lotteryId: Ulid,
    slug: z.string(),
    title: z.string(),
    mechanic: LotteryMechanic,
    kind: DrawKind,
    status: DrawStatus,
    lotteryStatus: z.string(),
    configVersion: z.number().int(),
    algorithm: z.literal(DRAW_ALGORITHM),
    seedCommit: HexHash256,
    committedAt: IsoDateTime,
    snapshotHash: HexHash256.nullable(),
    resultHash: HexHash256.nullable(),
    poolVectorHash: HexHash256.nullable(),
    poolSize: z.number().int().nullable(),
    publishedTermsHash: HexHash256.nullable(),
    redrawOfDrawId: Ulid.nullable(),
    operatorId: Ulid.nullable(),
    secondOperatorId: Ulid.nullable(),
    allocatedAt: IsoDateTime.nullable(),
    publishedAt: IsoDateTime.nullable(),
    revealedAt: IsoDateTime.nullable(),
    verifiedAt: IsoDateTime.nullable(),
    failedAt: IsoDateTime.nullable(),
    failureReason: z.string().nullable(),
    cancelledAt: IsoDateTime.nullable(),
    drawAt: IsoDateTime.nullable(),
    closesAt: IsoDateTime.nullable(),
    counts: z.object({
        eligible: z.number().int(),
        excluded: z.number().int(),
        winners: z.number().int(),
        sponsorSamples: z.number().int(),
        ticketsAssigned: z.number().int().nullable(),
    }),
    tiers: z.array(DrawTierStateDto),
    revealedTiers: z.number().int(),
    totalTiers: z.number().int(),
    /** §9.5 two-person control: prize value at or above the tenant threshold. */
    highValue: z.boolean(),
    highValueThresholdMinor: z.string(),
    bundleAvailable: z.boolean(),
    resultsUrl: z.string(),
    fairnessUrl: z.string(),
});
export const DrawListQuery = z.object({
    lotteryId: Ulid.optional(),
    status: DrawStatus.optional(),
});
export const DrawListDto = z.object({ items: z.array(DrawAdminDto) });
/** `POST /v1/admin/lotteries/:id/draw`. */
export const RunDrawRequest = z.object({
    /** Required for redraws (§9.4). */
    reason: z.string().max(2000).optional(),
    redrawOfDrawId: Ulid.optional(),
    /** Second operator for high-value draws (§9.5); must differ from the caller. */
    secondOperatorId: Ulid.optional(),
    /** `now` runs synchronously (default); `enqueue` hands the execution to the worker `draw` queue. */
    execute: z.enum(['now', 'enqueue']).default('now'),
});
export const RunDrawResponse = z.object({
    drawId: Ulid,
    lotteryId: Ulid,
    status: DrawStatus,
    enqueued: z.boolean(),
    winners: z.number().int(),
    sponsorSamples: z.number().int(),
    resultHash: HexHash256.nullable(),
    /** Already-committed result returned on a retry (Appendix A "draw retries after crash"). */
    replayed: z.boolean(),
});
export const RevealNextRequest = z.object({
    secondOperatorId: Ulid.optional(),
    /** Reveal every remaining tier at once (static fallback / rehearsal end). */
    all: z.boolean().default(false),
});
export const RevealNextResponse = z.object({
    drawId: Ulid,
    status: DrawStatus,
    revealedTiers: z.number().int(),
    totalTiers: z.number().int(),
    complete: z.boolean(),
    tier: PublicTierDto.nullable(),
});
export const PublishStaticResponse = z.object({
    drawId: Ulid,
    status: DrawStatus,
    resultsUrl: z.string(),
    fairnessUrl: z.string(),
    bundleUrl: z.string(),
    signatureKeyId: z.string(),
});
export const RedrawReasonCode = z.enum(['unclaimed', 'rejected_after_appeal', 'disqualified', 'other']);
/** `POST /v1/admin/draws/:id/redraw` — §9.4: reason, approved eligibility snapshot, new seed, new draw id. */
export const RedrawRequest = z.object({
    winId: z.string().min(8).max(20),
    reason: z.string().min(3).max(2000),
    reasonCode: RedrawReasonCode.default('other'),
    secondOperatorId: Ulid.optional(),
    evidenceRef: z.string().max(512).optional(),
});
export const RedrawResponse = z.object({
    redrawId: Ulid,
    originalWinId: z.string(),
    originalDrawId: Ulid.nullable(),
    newDrawId: Ulid,
    replacementWinId: z.string().nullable(),
    status: DrawStatus,
    eligibleEntries: z.number().int(),
});
export const DrawVerifyDto = z.object({
    drawId: Ulid,
    status: DrawStatus,
    ok: z.boolean(),
    kind: z.enum(['SCHEDULED', 'FIXED_POOL', 'NUMBER_PICK', 'UNKNOWN']),
    checks: z.array(z.object({ name: z.string(), ok: z.boolean(), detail: z.string().optional() })),
    mismatches: z.array(z.string()),
    verifierVersion: z.string(),
    checkedAt: IsoDateTime,
});
/** Reveal rehearsal (§16.2): the pacing sequence with counts only — no winners are disclosed. */
export const RevealRehearsalDto = z.object({
    drawId: Ulid,
    steps: z.array(z.object({
        index: z.number().int(),
        tier: PrizeTier,
        prizeName: z.string(),
        winners: z.number().int(),
        suggestedPauseMs: z.number().int(),
    })),
    totalWinners: z.number().int(),
    fallback: z.object({ resultsUrl: z.string(), note: z.string() }),
});
/** Outcome of `onEntryQualified` (instant mechanics / first-N), also served internally for re-checks. */
export const EntryOutcomeDto = z.object({
    entryId: Ulid,
    lotteryId: Ulid,
    outcome: z.enum(['WIN', 'LOSS', 'SPONSOR_SAMPLE', 'PENDING', 'NOT_APPLICABLE']),
    winId: z.string().nullable(),
    sampleWinId: z.string().nullable(),
    position: z.number().int().nullable(),
    prizeId: Ulid.nullable(),
    /** False when the call found an existing outcome (idempotent re-check). */
    created: z.boolean(),
});
/** Internal (worker → api) draw execution request/response. */
export const ExecuteDrawRequest = z.object({
    tenantId: Ulid,
    drawId: Ulid,
    /** The worker ran on-draw reconciliation first and it did not block (§10.3 step 5). */
    reconciliationChecked: z.boolean().default(false),
});
export const SamplingAwardDto = z.object({
    awardId: Ulid,
    ruleId: Ulid,
    ruleVersion: z.number().int(),
    participantSequence: z.number().int(),
    status: z.enum([
        'AWARDED',
        'SKIPPED_MAIN_WIN',
        'SKIPPED_OUT_OF_RANGE',
        'SKIPPED_CAP',
        'SKIPPED_NO_STOCK',
        'SKIPPED_INELIGIBLE',
    ]),
    winId: z.string().nullable(),
    evaluatedAt: IsoDateTime,
});
//# sourceMappingURL=outcome.js.map